import { Badge } from '@thegpvc/ui';
import { SectionCard } from './SectionCard';
import { cx, text } from '../styles/theme';
import type { CoachingInsight } from '../types/api';

const SEVERITY_VARIANT: Record<string, 'error' | 'warning' | 'neutral'> = {
  high: 'error',
  medium: 'warning',
  low: 'neutral',
};

/**
 * CoachingInsights renders the deterministic execution-coaching insights (missed
 * fan-out, cache waste, tool thrash) with their full detail, evidence, and
 * recommendation — the "how efficiently it ran" axis. Renders nothing when there
 * are no insights.
 *
 * With `heading` (the default), it wraps the list in a titled SectionCard for
 * standalone use; pass `heading={null}` to render a bare list for embedding
 * inside another card (e.g. a chapter), where the surrounding context already
 * provides the frame.
 */
export function CoachingInsights({
  insights,
  heading = 'Coaching',
}: {
  insights?: CoachingInsight[];
  heading?: string | null;
}) {
  if (!insights || insights.length === 0) return null;
  const list = (
    <ul className="space-y-4">
      {insights.map((it, i) => (
        <li key={`${it.type}-${i}`} className="space-y-1">
          <div className="flex items-center gap-2">
            <Badge variant={SEVERITY_VARIANT[it.severity] ?? 'neutral'}>{it.severity}</Badge>
            <span className={cx('font-medium', text.primary)}>{it.title}</span>
          </div>
          <p className={cx('text-sm', text.secondary)}>{it.detail}</p>
          {it.evidence && it.evidence.length > 0 && (
            <ul className={cx('list-disc space-y-0.5 pl-5 text-sm', text.secondary)}>
              {it.evidence.map((ev, j) => (
                <li key={j}>
                  <span className="font-mono text-xs">{ev}</span>
                </li>
              ))}
            </ul>
          )}
          <p className="text-sm">
            <span className={cx('font-medium', text.primary)}>Try: </span>
            <span className={text.secondary}>{it.recommendation}</span>
          </p>
        </li>
      ))}
    </ul>
  );
  if (heading === null) return list;
  return <SectionCard title={heading}>{list}</SectionCard>;
}
